<script setup>
import { ref, watch } from 'vue'
import SplitCodeInput from './SplitCodeInput.vue'

const props = defineProps({
  csrfToken: { type: String, required: true },
})
const emit = defineEmits(['redirect'])

const mode  = ref('totp') // 'totp' | 'recovery'
const code  = ref('')
const error = ref('')
const busy  = ref(false)

function useRecovery() {
  mode.value  = 'recovery'
  code.value  = ''
  error.value = ''
}
function useTotp() {
  mode.value  = 'totp'
  code.value  = ''
  error.value = ''
}

watch(mode, () => { code.value = '' })

async function submit() {
  if (busy.value) return
  // Require a fully-filled code before posting.
  const needed = mode.value === 'totp' ? 6 : 9  // 4 + dash + 4
  if (code.value.trim().length < needed) {
    error.value = 'Enter the full code.'
    return
  }
  busy.value  = true
  error.value = ''
  try {
    const fd = new FormData()
    fd.append('csrf_token', props.csrfToken)
    fd.append('mode', mode.value)
    fd.append('code', code.value.trim())
    const res  = await fetch('/api/auth/totp-verify', { method: 'POST', body: fd })
    const data = await res.json().catch(() => ({}))
    if (res.status === 429) {
      // Rate limit exhausted — session was invalidated server-side. Bounce
      // back to /login with a flash so the login screen explains the lockout.
      window.location.href = '/login?lockout=2fa'
      return
    }
    if (!res.ok || !data.ok) throw new Error(data.error || 'Verification failed.')
    emit('redirect', data.redirect || '/')
  } catch (e) {
    error.value = e.message || 'Verification failed.'
    code.value = ''
  } finally {
    busy.value = false
  }
}

// Submit automatically once the user fills the last cell — matches the
// "tap last digit and you're in" pattern people expect from 2FA UIs.
function onComplete() {
  if (!busy.value) submit()
}
</script>

<template>
  <form class="totp-step auth-card hf-glass" @submit.prevent="submit">
    <h1>Two-factor verification</h1>
    <p class="auth-intro" v-if="mode === 'totp'">Enter the 6-digit code from your authenticator app.</p>
    <p class="auth-intro" v-else>Enter one of your recovery codes (<code>XXXX-XXXX</code>). It will be marked used.</p>

    <label class="totp-step__label" for="totp-cell-0">
      {{ mode === 'totp' ? 'Authenticator code' : 'Recovery code' }}
    </label>
    <SplitCodeInput
      v-model="code"
      :format="mode === 'totp' ? 'digits' : 'recovery'"
      :disabled="busy"
      @complete="onComplete"
    />

    <p v-if="error" class="error" role="alert">{{ error }}</p>

    <button type="submit" class="hf-btn primary auth-submit" :disabled="busy">
      {{ busy ? 'Verifying…' : 'Verify' }}
    </button>

    <p class="totp-step__alt" v-if="mode === 'totp'">
      <button type="button" data-action="use-recovery" class="hf-link" @click="useRecovery">Use a recovery code instead</button>
    </p>
    <p class="totp-step__alt" v-else>
      <button type="button" data-action="use-totp" class="hf-link" @click="useTotp">Use authenticator app instead</button>
    </p>
  </form>
</template>
